import { getSegmentColor, SegmentedBar } from './SegmentedBar';
import styles from './VariantWeightEditor.module.css';

export interface Variant {
	key: string;
	id: string;
	name: string;
	weight: number;
}

interface VariantWeightEditorProps {
	variants: Variant[];
	onChange: (variants: Variant[]) => void;
	disabled?: boolean;
	minVariants?: number;
}

let variantKeyCounter = 0;
export function nextVariantKey() {
	variantKeyCounter += 1;
	return `wv${variantKeyCounter}`;
}

function nameToId(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, '_')
		.replace(/^_+|_+$/g, '');
}

export function VariantWeightEditor({
	variants,
	onChange,
	disabled = false,
	minVariants = 2,
}: VariantWeightEditorProps) {
	const total = variants.reduce((sum, v) => sum + v.weight, 0);
	const isValid = total === 10000;

	// Track which IDs have been manually edited (by key)
	// We use a module-level set approach: when user edits ID field directly, mark it

	function handleWeightChange(key: string, rawVal: string) {
		const n = parseInt(rawVal, 10);
		const weight = Number.isNaN(n) ? 0 : Math.max(0, Math.min(10000, n));
		onChange(variants.map((v) => (v.key === key ? { ...v, weight } : v)));
	}

	function handleNameChange(key: string, name: string) {
		onChange(
			variants.map((v) => {
				if (v.key !== key) return v;
				// Auto-suggest id from name only if id is currently derived from old name or empty
				const suggestedId = nameToId(v.name);
				const autoId = v.id === '' || v.id === suggestedId;
				return {
					...v,
					name,
					id: autoId ? nameToId(name) : v.id,
				};
			}),
		);
	}

	function handleIdChange(key: string, id: string) {
		onChange(variants.map((v) => (v.key === key ? { ...v, id } : v)));
	}

	function handleAddVariant() {
		onChange([...variants, { key: nextVariantKey(), id: '', name: '', weight: 0 }]);
	}

	function handleRemoveVariant(key: string) {
		if (variants.length <= minVariants) return;
		onChange(variants.filter((v) => v.key !== key));
	}

	return (
		<div className={styles.wrapper}>
			<SegmentedBar
				segments={variants.map((variant) => ({
					key: variant.key,
					label: variant.name || variant.id,
					value: variant.weight,
					title: `${variant.name || variant.id}: ${((variant.weight / 10000) * 100).toFixed(1)}%`,
				}))}
				denominator={10000}
			/>

			{/* Header row */}
			<div className={styles.tableHeader}>
				<span className={styles.colHeader}>ID</span>
				<span className={styles.colHeader}>Name</span>
				<span className={styles.colHeader}>Weight</span>
				<span className={styles.colHeader}>%</span>
				<span />
			</div>

			{/* Variant rows */}
			{variants.map((v, i) => {
				const color = getSegmentColor(i);
				const pct = ((v.weight / 10000) * 100).toFixed(1);
				return (
					<div key={v.key} className={styles.variantRow}>
						<div className={styles.colorDot} style={{ backgroundColor: color }} />
						<input
							className={[styles.rowInput, styles.mono].join(' ')}
							type="text"
							value={v.id}
							onChange={(e) => handleIdChange(v.key, e.target.value)}
							placeholder="control"
							disabled={disabled}
							required
						/>
						<input
							className={styles.rowInput}
							type="text"
							value={v.name}
							onChange={(e) => handleNameChange(v.key, e.target.value)}
							placeholder="Control"
							disabled={disabled}
							required
						/>
						<input
							className={[styles.rowInput, styles.mono].join(' ')}
							type="number"
							value={v.weight}
							onChange={(e) => handleWeightChange(v.key, e.target.value)}
							min={0}
							max={10000}
							disabled={disabled}
							required
						/>
						<span className={[styles.pctDisplay, styles.mono].join(' ')}>{pct}%</span>
						<button
							type="button"
							className={styles.removeBtn}
							onClick={() => handleRemoveVariant(v.key)}
							disabled={disabled || variants.length <= minVariants}
							aria-label={`Remove variant ${v.name || v.id}`}
						>
							✕
						</button>
					</div>
				);
			})}

			{/* Footer */}
			<div className={styles.footer}>
				{!disabled && (
					<button type="button" className={styles.addBtn} onClick={handleAddVariant}>
						+ Add variant
					</button>
				)}
				<span className={[styles.total, isValid ? styles.totalOk : styles.totalError].join(' ')}>
					Total: {total}/10000{isValid ? ' ✓' : ' ⚠'}
				</span>
			</div>
		</div>
	);
}
